import {
  formatShareCapitalAmount,
  parseShareCapitalAmount,
  sumEntityIssuedBaselineByCurrency,
} from './shareCapitalIssuedBaseline';

/**
 * SG Allotment preset — ShareholderList currency normalization.
 *
 * Rules (SG preset only):
 * - Group by `contactCd + currencyCd`: the same shareholder must not have the same
 *   currency in more than one row → merge into one (sum numberOfShares / paidUpShareCapital).
 * - One class per currency is assumed, so the first row's class fields are kept.
 * - `issuedShareCapital` is a company-level per-currency total
 *   (entity baseline for the currency + Σ paidUp of every shareholder in that currency),
 *   so all rows sharing a currency carry the identical issued value.
 *
 * This does NOT dedupe across different shareholders — different shareholders keep their
 * own rows, but their currency/issued values are unified per currency.
 */
export function mergeAllotmentShareholderListByCurrency(
  list: Array<Record<string, any>>,
  entityShares: Array<Record<string, unknown>> = [],
): Array<Record<string, any>> {
  if (!Array.isArray(list) || list.length === 0) return [];

  const currencyOf = (row: Record<string, any>): string =>
    String(row.currencyCd ?? row.currency?.code ?? '').trim();

  // 1) Merge same shareholder + same currency into a single row.
  const mergedByKey = new Map<string, Record<string, any>>();
  const order: string[] = [];

  for (const row of list) {
    const contactCd = String(row.contactCd ?? '').trim();
    const currencyCd = currencyOf(row);
    const key = `${contactCd}::${currencyCd}`;

    const existing = mergedByKey.get(key);
    if (!existing) {
      mergedByKey.set(key, { ...row });
      order.push(key);
      continue;
    }

    existing.numberOfShares = formatShareCapitalAmount(
      parseShareCapitalAmount(existing.numberOfShares) +
        parseShareCapitalAmount(row.numberOfShares),
    );
    existing.paidUpShareCapital = formatShareCapitalAmount(
      parseShareCapitalAmount(existing.paidUpShareCapital) +
        parseShareCapitalAmount(row.paidUpShareCapital),
    );
  }

  const mergedRows = order.map((k) => mergedByKey.get(k) as Record<string, any>);

  // 2) Company-level per-currency issued total = entity baseline + Σ paidUp (all shareholders).
  const paidUpByCurrency = new Map<string, number>();
  for (const row of mergedRows) {
    const currencyCd = currencyOf(row);
    paidUpByCurrency.set(
      currencyCd,
      (paidUpByCurrency.get(currencyCd) || 0) +
        parseShareCapitalAmount(row.paidUpShareCapital),
    );
  }

  const issuedByCurrency = new Map<string, string>();
  for (const [currencyCd, totalPaidUp] of paidUpByCurrency.entries()) {
    const baseline = sumEntityIssuedBaselineByCurrency(entityShares, currencyCd);
    issuedByCurrency.set(currencyCd, formatShareCapitalAmount(baseline + totalPaidUp));
  }

  // 3) Unify issuedShareCapital across all rows of the same currency.
  for (const row of mergedRows) {
    const currencyCd = currencyOf(row);
    const issued = issuedByCurrency.get(currencyCd);
    if (issued !== undefined) {
      row.issuedShareCapital = issued;
    }
  }

  return mergedRows;
}
